/**
 * Shrinks the build-stream transcript before each POST (WC-7, P3.4 in
 * PROTOTYPER_STABILITY_TECH_PLAN.md).
 *
 * Within a turn, every continuation round appends its tool calls to the same assistant message,
 * so each POST used to resend the full body of every file written so far. For completed tool
 * calls in steps *before* the latest `step-start` part:
 * - `writeFile` keeps its `path`; `content` becomes a short placeholder with the length and a
 *   hash, telling the model to `readFile` if it needs the file.
 * - long `build` output keeps its head and tail.
 *
 * The newest step stays intact because the model continues directly from those calls. This is
 * safe for Chef: it saves files from the model's own stream (`step.toolCalls` in
 * `chef/app/lib/.server/prototyper/generator.ts`), never from this transcript, and `readFile`
 * runs server-side against those saved files. Only the request body changes; the `useChat`
 * messages shown in the UI are untouched.
 */

/** Bodies shorter than this are cheaper to keep than to describe. */
export const TRIM_MIN_CONTENT_CHARS = 400;
const BUILD_OUTPUT_KEEP_CHARS = 1_500;
const BUILD_OUTPUT_TRIM_ABOVE_CHARS = 4_000;

type TranscriptMessage = { role: string; parts?: readonly unknown[] };

/** FNV-1a, 32-bit hex: enough to tell the model two omitted bodies apart. */
function shortHash(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

function toolNameOf(part: Record<string, unknown>): string | null {
  if (part.type === 'dynamic-tool') return typeof part.toolName === 'string' ? part.toolName : null;
  if (typeof part.type === 'string' && part.type.startsWith('tool-')) return part.type.slice(5);
  return null;
}

export function omittedContentPlaceholder(path: string, content: string): string {
  return (
    `[omitted from this request: ${content.length} characters already written to ${path} ` +
    `in an earlier step (hash ${shortHash(content)}). Call readFile("${path}") if you need ` +
    `the current content.]`
  );
}

function trimToolPart(part: unknown): unknown {
  if (!part || typeof part !== 'object') return part;
  const p = part as Record<string, unknown>;
  // Only calls that finished: a pending call's input is what the client is about to run.
  if (p.state !== 'output-available') return part;
  const toolName = toolNameOf(p);

  if (toolName === 'writeFile' && p.input && typeof p.input === 'object') {
    const input = p.input as Record<string, unknown>;
    const content = input.content;
    const path = typeof input.path === 'string' ? input.path : 'the file';
    if (typeof content !== 'string' || content.length < TRIM_MIN_CONTENT_CHARS) return part;
    return { ...p, input: { ...input, content: omittedContentPlaceholder(path, content) } };
  }

  if (
    toolName === 'build' &&
    typeof p.output === 'string' &&
    p.output.length > BUILD_OUTPUT_TRIM_ABOVE_CHARS
  ) {
    const output = p.output;
    const omitted = output.length - 2 * BUILD_OUTPUT_KEEP_CHARS;
    return {
      ...p,
      output:
        `${output.slice(0, BUILD_OUTPUT_KEEP_CHARS)}\n…[${omitted} characters of an earlier build ` +
        `omitted]…\n${output.slice(-BUILD_OUTPUT_KEEP_CHARS)}`,
    };
  }

  return part;
}

function lastStepStartIndex(parts: readonly unknown[]): number {
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i] as { type?: unknown } | null;
    if (part && part.type === 'step-start') return i;
  }
  return -1;
}

export function trimTranscriptForRequest<M extends TranscriptMessage>(messages: readonly M[]): M[] {
  let lastAssistant = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]!.role === 'assistant') {
      lastAssistant = i;
      break;
    }
  }

  return messages.map((message, index) => {
    if (message.role !== 'assistant' || !Array.isArray(message.parts)) return message;
    // In the newest assistant message, keep the latest step (from its last `step-start`).
    const keepFrom =
      index === lastAssistant ? lastStepStartIndex(message.parts) : message.parts.length;
    if (keepFrom <= 0) return message;

    let changed = false;
    const parts = message.parts.map((part, partIndex) => {
      if (partIndex >= keepFrom) return part;
      const next = trimToolPart(part);
      if (next !== part) changed = true;
      return next;
    });
    return changed ? { ...message, parts } : message;
  });
}
